import { toast } from 'react-toastify';
import debug from '@/utils/debug';

const errorDebug = debug.scope('error-handler');

export enum ErrorCategory {
  NETWORK = 'NETWORK',
  AUTH = 'AUTH',
  PERMISSION = 'PERMISSION',
  VALIDATION = 'VALIDATION',
  DATA = 'DATA',
  UNKNOWN = 'UNKNOWN'
}

interface ErrorConfig {
  showToast?: boolean;
  logToConsole?: boolean;
  duration?: number;
  category?: ErrorCategory;
}

class ErrorHandler {
  private isOnline = true;
  private connectionCheckInterval: NodeJS.Timeout | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.setupConnectionMonitoring();
    }
  }

  private setupConnectionMonitoring() {
    // Monitor online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      toast.success('Connection restored', {
        position: 'bottom-center',
        autoClose: 3000
      });
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      toast.error('Connection lost. Some features may be unavailable.', {
        position: 'bottom-center',
        autoClose: false
      });
    });
  }

  /**
   * Categorize error based on type and status
   */
  private categorizeError(error: any): ErrorCategory {
    if (!navigator.onLine || !this.isOnline) {
      return ErrorCategory.NETWORK;
    }

    if (error.response) {
      const status = error.response.status;
      if (status === 401) return ErrorCategory.AUTH;
      if (status === 403) return ErrorCategory.PERMISSION;
      if (status >= 400 && status < 500) return ErrorCategory.VALIDATION;
      if (status >= 500) return ErrorCategory.DATA;
    }

    if (error.code === 'ECONNABORTED' || error.code === 'ECONNREFUSED') {
      return ErrorCategory.NETWORK;
    }

    return ErrorCategory.UNKNOWN;
  }

  /**
   * Get user-friendly error message
   */
  private getUserMessage(error: any, category: ErrorCategory): string {
    // Check for custom error message from backend
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    // Default messages by category
    switch (category) {
      case ErrorCategory.NETWORK:
        if (!navigator.onLine || !this.isOnline) {
          return 'No internet connection. Please check your network.';
        }
        if (error.code === 'ECONNABORTED') {
          return 'Request timed out. Please try again.';
        }
        return 'Connection failed. Please check if the service is available.';

      case ErrorCategory.AUTH:
        return 'Your session has expired. Please sign in again.';

      case ErrorCategory.PERMISSION:
        return 'You don\'t have permission to perform this action.';

      case ErrorCategory.VALIDATION:
        const status = error.response?.status;
        if (status === 404) {
          return 'The requested data was not found.';
        }
        if (status === 400) {
          return 'Invalid request. Please check your input.';
        }
        return 'There was a problem with your request.';

      case ErrorCategory.DATA:
        return 'Server error. Please try again later.';

      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Main error handling method
   */
  handle(error: any, context?: string, config?: ErrorConfig) {
    const category = config?.category || this.categorizeError(error);
    const userMessage = this.getUserMessage(error, category);

    // Log to console for developers
    if (config?.logToConsole !== false) {
      errorDebug.error(`[${context || 'Unknown Context'}] ${category}:`, {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        stack: error.stack
      });
    }

    // Show toast to user
    if (config?.showToast !== false) {
      const toastConfig = {
        autoClose: config?.duration || 5000,
        position: 'top-right' as const
      };

      switch (category) {
        case ErrorCategory.AUTH:
          toast.error(userMessage, {
            ...toastConfig,
            autoClose: false, // Don't auto-close auth errors
            onClick: () => {
              // Redirect to login
              window.location.href = '/login';
            }
          });
          break;

        case ErrorCategory.NETWORK:
          toast.error(userMessage, {
            ...toastConfig,
            position: 'bottom-center' as const
          });
          break;

        default:
          toast.error(userMessage, toastConfig);
      }
    }

    return {
      category,
      userMessage,
      originalError: error
    };
  }

  /**
   * Handle specific API errors
   */
  handleApiError(error: any, endpoint: string) {
    const context = `API: ${endpoint}`;

    // Special handling for certain endpoints
    if (endpoint.includes('/impressions') || endpoint.includes('/clicks')) {
      return this.handle(error, context, {
        showToast: true,
        duration: 3000
      });
    }

    return this.handle(error, context);
  }

  /**
   * Handle data loading errors
   */
  handleDataError(error: any, dataType: string) {
    const context = `Loading ${dataType}`;
    const userMessage = `Failed to load ${dataType}. Some features may be limited.`;

    toast.warning(userMessage, {
      autoClose: 4000,
      position: 'top-center'
    });

    errorDebug.warn(context, error);

    return {
      category: ErrorCategory.DATA,
      userMessage,
      originalError: error
    };
  }

  /**
   * Handle silent errors (log only, no toast)
   */
  handleSilent(error: any, context?: string) {
    return this.handle(error, context, {
      showToast: false,
      logToConsole: true
    });
  }

  /**
   * Show loading timeout warning
   */
  showTimeoutWarning(operation: string, duration: number) {
    if (duration > 30000) {
      toast.warning(`${operation} is taking longer than expected. You may want to refresh if this continues.`, {
        autoClose: false,
        position: 'top-center'
      });
    } else if (duration > 10000) {
      toast.info(`${operation} is taking longer than expected...`, {
        autoClose: 5000,
        position: 'top-center'
      });
    }
  }

  /**
   * Clear all error toasts
   */
  clearErrors() {
    toast.dismiss();
  }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

export default errorHandler;